import { faShoppingBag } from '@fortawesome/pro-regular-svg-icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Chip } from 'src/components/Chip';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { CartItem as CartItemType } from 'src/models/cart/_types';
import { Cart } from 'src/models/cart/_types';
import { User } from 'src/models/user/_types';
import { getUserDisplayName, getUserInitials } from 'src/models/user/utils';
import { NoResults } from 'src/shared/NoResults';

import { CartItem } from '../../pages/Clients/PaymentDialog/CartItem';
import { UserSelectorChip } from '../../pages/Clients/PaymentDialog/UserSelectorChip';


interface SharedBagProps {
  // User(s) display
  user?: User;
  // Items to display
  items: CartItemType[];
  // Item management callbacks
  onRemove: (item: CartItemType, index?: number) => void;
  onEdit?: (item: CartItemType) => void;
  onUpdateItem?: (updatedItem: CartItemType) => void;
  // UI customization
  emptyTitle?: string;
  itemCountLabel?: string;
  // User selector chip (for payment dialog)
  showUserSelector?: boolean;
  onChangeUser?: () => void;
  // Cart for inventory tracking (optional - if not provided, inventory features disabled)
  cart?: Cart | null;
  // Item Settings Component
  ItemSettingsComponent?: React.ComponentType<{
    editing?: boolean;
    item: CartItemType;
    itemType?: any;
    onSubmit: (updatedItem: CartItemType) => void;
    user?: any;
  }>;
}

export const SharedBag: React.FC<SharedBagProps> = ({
  user,
  items,
  onRemove,
  onEdit,
  onUpdateItem,
  emptyTitle,
  itemCountLabel,
  showUserSelector = false,
  onChangeUser,
  cart = null,
  ItemSettingsComponent,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'client.payment' });

  const handlePaymentOptionChange = (
    item: CartItemType,
    optionId: string | undefined
  ) => {
    if (!item.item?.paymentOptions || !onUpdateItem) return;

    const newOption = item.item.paymentOptions.find(
      (opt) => opt.id === optionId
    );
    if (newOption) {
      const updatedItem = { ...item, paymentOption: newOption };
      onUpdateItem(updatedItem);
    }
  };

  const handleQuantityChange = (item: CartItemType, newQuantity: number) => {
    if (!onUpdateItem) return;

    if (newQuantity === 0) {
      const index = items.findIndex((i) => i.id === item.id);
      onRemove(item, index);
      return;
    }
    onUpdateItem({ ...item, quantity: newQuantity });
  };

  return (
    <Box>
      {/* User Header */}
      {user && (
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', my: 1 }}
        >
          {showUserSelector && onChangeUser ? (
            <UserSelectorChip
              user={user}
              onChangeUser={onChangeUser}
              size="medium"
            />
          ) : (
            <Stack direction="row" sx={{ alignItems: 'center' }}>
              <Avatar
                src={user.profileImage}
                initials={getUserInitials(user)}
                sx={{ mr: 2 }}
              />
              <Typography variant="subtitle1" noWrap>
                {getUserDisplayName(user)}
              </Typography>
            </Stack>
          )}

          <Chip
            variant="outlined"
            color="primary"
            label={
              itemCountLabel ||
              `${items.length} item${items.length === 1 ? '' : 's'}`
            }
          />
        </Stack>
      )}
      {/* Items */}
      <Stack spacing={2} sx={{ mt: 3 }}>
        {!items.length && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 4,
            }}
          >
            <NoResults
              title={emptyTitle || t('emptyBag')}
              icon={faShoppingBag}
              iconSize="large"
            />
          </Box>
        )}
        {items.map((item, index) => {
          return (
            <CartItem
              key={`${item.itemId}-${item.itemType}-${index}`}
              editable={true}
              item={item}
              onRemove={onRemove}
              index={index}
              onEdit={onEdit}
              onChangeQuantity={(newQuantity) =>
                handleQuantityChange(item, newQuantity)
              }
              onPaymentOptionChange={handlePaymentOptionChange}
              cart={cart}
              ItemSettingsComponent={ItemSettingsComponent}
            />
          );
        })}
      </Stack>
    </Box>
  );
};
